import { memo, useCallback, useMemo } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { QueryRule as QueryRuleType, QueryField, OperatorValue } from '../../../../types/QueryBuilder';
import { getFieldsByCategory, getOperatorsForField } from '../../../../utils/queryBuilderUtils';
import { useLanguage } from '../../../../context/LanguageContext';
import ValueEditor from './ValueEditors';

// Keeps the select at MUI's standard single-line "small" height: a label longer than the control's
// width (see measureWidth below) is ellipsis-truncated rather than wrapping/growing the control.
// The font also steps down at narrow widths — same rule, same sx keys, on both mobile and desktop —
// instead of only trimming widths, so the row overall reads consistently at either size.
const selectDisplaySx = {
    fontSize: { xs: '0.78rem', sm: '0.875rem' },
    '& .MuiSelect-select': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
};

const selectLabelSx = { fontSize: { xs: '0.78rem', sm: '0.875rem' } };

// Sizes a select to its widest current option (in `ch`, ~1 average character) instead of a fixed
// pixel guess, so Catégorie/Champ/Opérateur each take just the width their own content needs. The
// cap itself is a CSS clamp() tied to the viewport (not a fixed 260px) so it shrinks in step with
// the rest of the row on a narrow/responsive layout instead of only ever being capped at desktop
// size — selectDisplaySx then ellipsis-truncates a label that still doesn't fit.
const measureWidth = (labels: string[]): string => `min(${Math.max(4, ...labels.map(l => l.length)) + 4}ch, clamp(110px, 34vw, 260px))`;

// getOperatorsForField's labels are plain English ('equals', 'before', ...) with no i18n of their
// own — reusing the same pages.clients.queryBuilder.operators.* keys the summary view already
// relies on (see summaryBuilders.ts's OPERATOR_LABEL_KEY) keyed by label text, since the same
// operator value ('<'/'>') is labelled differently per field type (before/after vs less/greater).
const OPERATOR_LABEL_TO_KEY: Record<string, string> = {
    'equals': 'equals',
    'not equals': 'notEquals',
    'greater than': 'greaterThan',
    'less than': 'lessThan',
    'greater or equal': 'greaterOrEqual',
    'less or equal': 'lessOrEqual',
    'between': 'between',
    'before': 'before',
    'after': 'after',
    'in': 'in',
    'not in': 'notIn',
    'contains': 'contains',
    'does not contain': 'notContains',
};

interface QueryRuleProps {
    rule: QueryRuleType;
    categories: string[];
    fields: QueryField[];
    onUpdateRule: (ruleId: string, data: Partial<QueryRuleType>) => void;
    onRemoveRule: (ruleId: string) => void;
}

const QueryRule = memo(function QueryRule({
    rule,
    categories,
    fields,
    onUpdateRule,
    onRemoveRule,
}: QueryRuleProps) {
    const { t } = useLanguage();
    const ruleId = rule.id;
    const field = useMemo(
        () => fields.find(f => f.name === rule.field && f.Categorie === rule.category),
        [fields, rule.field, rule.category]
    );
    const availableFields = useMemo(
        () => rule.category ? getFieldsByCategory(fields, rule.category) : [],
        [fields, rule.category]
    );
    const operators = useMemo(() => getOperatorsForField(field), [field]);

    const categoryWidth = useMemo(() => measureWidth(categories), [categories]);
    const fieldWidth = useMemo(() => measureWidth(availableFields.map(f => f.label)), [availableFields]);
    const operatorLabels = useMemo(
        () => operators.map(op => t(`pages.clients.queryBuilder.operators.${OPERATOR_LABEL_TO_KEY[op.label] ?? op.label}`)),
        [operators, t]
    );
    const operatorWidth = useMemo(() => measureWidth(operatorLabels), [operatorLabels]);

    const handleCategoryChange = useCallback((category: string) => {
        const newFields = getFieldsByCategory(fields, category);
        const newField = newFields[0]?.name || '';
        onUpdateRule(ruleId, {
            category,
            field: newField,
            operator: '=',
            value: '',
            valueTo: undefined
        });
    }, [fields, onUpdateRule, ruleId]);

    const handleFieldChange = useCallback((fieldName: string) => {
        const newField = fields.find(f => f.name === fieldName && f.Categorie === rule.category);
        onUpdateRule(ruleId, {
            field: fieldName,
            operator: (newField?.defaultOperator as OperatorValue) || '=',
            value: '',
            valueTo: undefined
        });
    }, [fields, rule.category, onUpdateRule, ruleId]);

    const handleOperatorChange = useCallback((operator: OperatorValue) => {
        onUpdateRule(ruleId, {
            operator,
            ...(operator !== 'between' ? { valueTo: undefined } : {})
        });
    }, [onUpdateRule, ruleId]);

    const handleValueChange = useCallback(
        (value: QueryRuleType['value']) => onUpdateRule(ruleId, { value }),
        [onUpdateRule, ruleId]
    );
    const handleRemove = useCallback(() => onRemoveRule(ruleId), [onRemoveRule, ruleId]);
    const handleToggleLock = useCallback(
        () => onUpdateRule(ruleId, { locked: !rule.locked }),
        [onUpdateRule, ruleId, rule.locked]
    );

    const isLocked = !!rule.locked;

    const renderValueEditor = () => {
        if (!field) return null;

        return (
            <Box
                sx={{
                    // Was `maxWidth: calc(100% - 565px)` — a fixed pixel subtraction that could go
                    // negative (clamping to minWidth, forcing overflow) on a narrow/responsive
                    // container. flex-basis + the wrap on the parent row already handle sizing.
                    flex: '1 1 240px',
                    minWidth: '200px',
                    maxWidth: '100%',
                    ...(isLocked ? { pointerEvents: 'none', opacity: 0.6 } : {}),
                }}
            >
                <ValueEditor
                    ruleId={rule.id}
                    field={field}
                    operator={rule.operator || '='}
                    value={rule.value}
                    onChange={handleValueChange}
                />
            </Box>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 1,
                mb: 2,
                p: { xs: 1, sm: 1.5 },
                bgcolor: '#ffffff',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                '&:hover': {
                    borderColor: '#556ee6',
                    boxShadow: '0 2px 8px rgba(85, 110, 230, 0.1)'
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Tooltip title={t(isLocked ? 'pages.clients.queryBuilder.unlockRule' : 'pages.clients.queryBuilder.lockRule')}>
                    <IconButton
                        size="small"
                        onClick={handleToggleLock}
                        sx={{
                            color: isLocked ? '#556ee6' : '#6c757d',
                            '&:hover': {
                                backgroundColor: 'rgba(85, 110, 230, 0.1)'
                            }
                        }}
                    >
                        {isLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Selects + value editor: wraps freely as its own group, however tall it grows, while
                the icon column (a sibling, below) stays pinned to the right and vertically centered
                on the row's full height via align-items: stretch + center — instead of drifting to
                wherever the wrap happened to leave them. */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1, flex: 1, minWidth: 0 }}>
            {/* Catégorie/Champ/Opérateur, each sized to its own content via measureWidth (capped so
                a long label can't force horizontal overflow) and kept at MUI's standard single-line
                select height (selectDisplaySx ellipsis-truncates instead of wrapping/growing). Kept
                on one line (nowrap) so the trio never splits onto separate rows. */}
            <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 1, maxWidth: '100%' }}>
                <FormControl size="small" sx={{ width: categoryWidth, maxWidth: '100%', flexShrink: 1 }}>
                    <InputLabel sx={selectLabelSx}>{t('pages.clients.queryBuilder.category')}</InputLabel>
                    <Select
                        value={rule.category || ''}
                        label={t('pages.clients.queryBuilder.category')}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        disabled={isLocked}
                        sx={selectDisplaySx}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 300,
                                },
                            },
                        }}
                    >
                        {categories.map((cat) => (
                            <MenuItem key={cat} value={cat}>
                                {cat}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ width: fieldWidth, maxWidth: '100%', flexShrink: 1 }}>
                    <InputLabel sx={selectLabelSx}>{t('pages.clients.queryBuilder.field')}</InputLabel>
                    <Select
                        value={rule.field || ''}
                        label={t('pages.clients.queryBuilder.field')}
                        onChange={(e) => handleFieldChange(e.target.value)}
                        disabled={!rule.category || isLocked}
                        sx={selectDisplaySx}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 300,
                                },
                            },
                        }}
                    >
                        {availableFields.map((f) => (
                            <MenuItem key={f.name} value={f.name}>
                                {f.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ width: operatorWidth, maxWidth: '100%', flexShrink: 1 }}>
                    <InputLabel sx={selectLabelSx}>{t('pages.clients.queryBuilder.operator')}</InputLabel>
                    <Select
                        value={rule.operator || ''}
                        label={t('pages.clients.queryBuilder.operator')}
                        onChange={(e) => handleOperatorChange(e.target.value as OperatorValue)}
                        disabled={!rule.field || isLocked}
                        sx={selectDisplaySx}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 300,
                                },
                            },
                        }}
                    >
                        {operators.map((op, index) => (
                            <MenuItem key={op.value} value={op.value}>
                                {operatorLabels[index]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {rule.field && rule.operator && renderValueEditor()}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexShrink: 0 }}>
                <Tooltip title={t('pages.clients.queryBuilder.deleteRule')}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleRemove}
                            disabled={isLocked}
                            sx={{
                                color: '#dc3545',
                                '&:hover': {
                                    backgroundColor: 'rgba(220, 53, 69, 0.1)'
                                }
                            }}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
});

export default QueryRule;
